import React, { useCallback, useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { useTypedDispatch, useTypedSelector } from '../../../redux/hooks';
import {
    checkCallFetcher,
    selectAllUserStatus,
    selectCallWhenAvailableList,
    selectParkingSlots,
    selectUserAuth,
    setNewFetchUsers,
    setStatusApi,
    statusSlice,
    tryUpdateLocations,
    updateUserStatus,

} from '../../../redux/slices';

import { IParkingSlot, IUserStatus } from '../../../types';
import { checkPresenceConnectedHarsh, initialisePresenceSocket, subscribeUserPresence } from '../../../redux/services/presneceApi';
import { selectQueuePanelUserSipNames } from '../../../redux/slices/queueSlice';

interface Props { }

const UserStatusController: React.FC<Props> = () => {
    const { username, password } = useTypedSelector(selectUserAuth);
    const allUserStatus = useTypedSelector(selectAllUserStatus);
    const parkingSlotNumbers: number[] = useTypedSelector(state => (selectParkingSlots(state) || []).map((slot: IParkingSlot) => slot.slot_number));
    const whenAvailableList = useTypedSelector(selectCallWhenAvailableList);
    const queuePanelUserNames = useTypedSelector(selectQueuePanelUserSipNames)

    const previousGetList = useRef<string[]>([]);
    const isFetching = useRef<boolean>(false);

    const dispatch = useTypedDispatch();

    const updateUserPresence = useCallback((presence: IUserStatus) => {
        if (presence.sipuser === username) {
            dispatch(checkCallFetcher(presence));

            if (Array.isArray(presence.registrations)) {
                dispatch(tryUpdateLocations(presence.registrations));
            }
        }

        dispatch(updateUserStatus(presence));
    }, [username]);

    useEffect(() => {
        initialisePresenceSocket(username, password, updateUserPresence)
            .then(() => {
                console.debug("Presence socket active");
            })
            .catch(e => {
                console.error("Failed to connect to status socket", e);
            });
    }, [username, password, updateUserPresence]);

    const handleSubscribe = () => {
        const whenAvailableRes: string[] = [];

        whenAvailableList.forEach((item) => {
            if (!allUserStatus[item.sip_name]) {
                whenAvailableRes.push(item.sip_name);
            }
        });

        const finalArr: string[] = [username].concat(allUserStatus).concat(whenAvailableRes).concat(queuePanelUserNames);

        const newUserList = finalArr.filter(u => !previousGetList.current.includes(u));

        dispatch(setNewFetchUsers(newUserList));
        dispatch(setStatusApi('fetching'));

        isFetching.current = true;

        subscribeUserPresence(
            finalArr,
            parkingSlotNumbers
        )
            .then(r => {
                dispatch(setStatusApi('success'));
                dispatch(statusSlice.actions.setUserPresnece(r));
                previousGetList.current = finalArr;
            })
            .catch(e => {
                Sentry.captureMessage('Failed subscribe_user_status', e);
                console.error("Error on status subscription", e);
                previousGetList.current = [];
                dispatch(setStatusApi('error'));
            })
            .finally(() => {
                isFetching.current = false
            });
    };

    useEffect(() => {
        let timer: NodeJS.Timeout | undefined;

        const trySubscribe = () => {
            if (checkPresenceConnectedHarsh() && !isFetching.current) {
                handleSubscribe();
                return true;
            }
            if (isFetching.current) {
                console.warn("Trying to subscribe presence when: already subscribing");
            }
            return false;
        };

        if (!trySubscribe()) {
            timer = setInterval(() => {
                if (trySubscribe()) {
                    clearInterval(timer);
                }
            }, 1_000);
        }

        return () => {
            if (timer) clearInterval(timer);
        };
    }, [allUserStatus, parkingSlotNumbers.length]);

    return null;
};

export default UserStatusController;
